import React, { useCallback, useMemo, useRef } from 'react'
import { VariableSizeList as ListBase } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import InfiniteLoaderBase from 'react-window-infinite-loader'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import Item from './viewSelectModalVariable.scrollItem'
import { Expression } from '~/prix/types/expression'
import { VariableWithConclusion } from '~/packages/general/variable/variables.typing'
import { FilterableByShape } from './filterableByField.util'

const InfiniteLoader = InfiniteLoaderBase as unknown as React.ComponentType<any>
const List = ListBase as unknown as React.ComponentType<any>

interface InfiniteScrollProps {
  variables: Array<VariableWithConclusion>
  currentBaseLayer: Layer
  handleSelectVariable: (variable: VariableWithConclusion) => void
  getCollapsedOpen: (isOpen: boolean, key: string | undefined) => void
  collapsedStates: { [key: string]: boolean }
  setIndexes: React.Dispatch<React.SetStateAction<{ startIndex: number; endIndex: number }>>
  nextPages: number
  apiIsLoading: boolean
  setIsLoadingMore: React.Dispatch<React.SetStateAction<boolean>>
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  filters: Expression[] | undefined
  selectedVariableIsFilterableBy: FilterableByShape | undefined
}

function InfiniteScroll({
  variables,
  currentBaseLayer,
  handleSelectVariable,
  getCollapsedOpen,
  collapsedStates,
  setIndexes,
  nextPages,
  apiIsLoading,
  setIsLoadingMore,
  getFilter,
  filters,
  selectedVariableIsFilterableBy,
}: InfiniteScrollProps) {
  const hasNextPages = nextPages > 0
  const itemCount = !apiIsLoading && hasNextPages ? variables.length + 1 : variables.length
  const isItemLoaded = useCallback(
    (index: number) => {
      return index < variables.length
    },
    [variables.length],
  )
  const listRef = useRef<any>(null)
  const initialSize = 90
  const sizes = useMemo(
    () => new Array(variables.length).fill(initialSize),
    [variables.length, initialSize],
  )

  const getItemSize = (index: number) => {
    return sizes[index]
  }
  const loadMoreItems = useCallback(
    (startIndex: number) => {
      setIndexes({ startIndex, endIndex: startIndex + 50 })
      setIsLoadingMore(true)
    },
    [setIndexes, setIsLoadingMore],
  )

  const setRefs = useCallback(
    (instance: any, loaderRef: ((instance: any) => void) | { current?: any } | null) => {
      listRef.current = instance

      if (typeof loaderRef === 'function') {
        loaderRef(instance)
        return
      }

      if (loaderRef && 'current' in loaderRef) {
        ;(loaderRef as React.MutableRefObject<any>).current = instance
      }
    },
    [],
  )

  return (
    <AutoSizer>
      {({ height, width }) => (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={0}
        >
          {({ onItemsRendered, ref }: any) => (
            <List
              height={height}
              itemCount={itemCount}
              itemSize={getItemSize}
              width={width}
              onItemsRendered={onItemsRendered}
              ref={(instance: any) => setRefs(instance, ref)}
            >
              {({ index, style }: any) => (
                <Item
                  index={index}
                  style={style}
                  isItemLoaded={isItemLoaded}
                  variables={variables}
                  currentBaseLayer={currentBaseLayer}
                  handleSelectVariable={handleSelectVariable}
                  collapsedStates={collapsedStates}
                  listRef={listRef}
                  sizes={sizes}
                  getCollapsedOpen={getCollapsedOpen}
                  selectedVariableIsFilterableBy={selectedVariableIsFilterableBy}
                  isApiLoading={apiIsLoading}
                  getFilter={getFilter}
                  filters={filters}
                />
              )}
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  )
}

export default InfiniteScroll
